import { useCallback, useEffect, useState } from 'react';

/**
 * Filter given search params from the given path
 * @param pathWithParams
 * @param searchParamsToKeep
 */
export const filterSearchParamsFromPath = (pathWithParams, searchParamsToKeep = []) => {
  const url = new URL(pathWithParams, 'https://www.udemy.com');
  const params = url.searchParams;
  const keys = Array.from(params.keys());
  const keysToRemove = keys.filter(key => !searchParamsToKeep.includes(key));
  keysToRemove.forEach(key => params.delete(key));
  return url.pathname + url.search;
};

/**
 * Constructing the Udemy's canonical URL from given URL with search params to keep
 * In default, it will remove all the search params except the ones in searchParamsToKeep array.
 *
 * If defaultLocale is given, it will remove the default locale from the path. We're not using defaultLocale in general
 * on our canonical urls. For example, we're not including "en" in our canonical urls.
 *
 * @param url
 * @param searchParamsToKeep
 * @param defaultLocale
 */
export const constructCanonicalUrl = (url, searchParamsToKeep = [], defaultLocale) => {
  const urlToFilter = new URL(url);
  let pathAndParamsToFilter = urlToFilter.pathname + urlToFilter.search;

  // Scrape default locale from the path
  if (defaultLocale) {
    pathAndParamsToFilter = pathAndParamsToFilter.replace(`/${defaultLocale}/`, '/');
  }
  const filteredPathAndParams = filterSearchParamsFromPath(pathAndParamsToFilter, searchParamsToKeep);
  return urlToFilter.origin + filteredPathAndParams;
};

/**
 * A hook that construct canonical URL in the server side and updates on the client side when component mounted for
 * next.js apps.
 *
 * Until component mount on the client side, it will try to construct the canonical url from given baseUrl and router
 * after mount, it will use window.location.href to construct the canonical url.
 *
 * @param router object that consists of the locale and asPath which describe the current page's path with its search params
 * i.e /courses/test?p=1
 * @param searchParamsToKeep search params to keep in the canonical url, if not give, it will remove all search params
 * @param baseUrl base url to construct the canonical url, if not given, it will return empty string
 * @param overrideDefaultLocale override the default locale that will be removed from the canonical url if it exists. Default is 'en'
 */
export const useCanonicalUrl = (router, searchParamsToKeep = [], baseUrl, overrideDefaultLocale) => {
  const locale = router.locale;
  const defaultLocale = overrideDefaultLocale !== null && overrideDefaultLocale !== void 0 ? overrideDefaultLocale : 'en';
  const domain = baseUrl !== null && baseUrl !== void 0 ? baseUrl : 'https://www.udemy.com/';
  const getCanonicalUrl = useCallback(url => constructCanonicalUrl(url, searchParamsToKeep, defaultLocale), [searchParamsToKeep, defaultLocale]);

  // Return the initial URL that will be returned when this hook called for the first time
  const getInitialUrl = () => {
    // This is purely handling trailing slash for the URLs both have or don't have trailing slash
    const url = domain.trim().replace(/\/$/, '') + '/';
    const pathWithSearchParams = router.asPath;
    return getCanonicalUrl(`${url}${locale}${pathWithSearchParams}`);
  };
  const [url, setUrl] = useState(getInitialUrl());
  useEffect(() => {
    // If we're in the client, update the canonical URL with the current window location
    setUrl(getCanonicalUrl(window.location.href));
  }, [getCanonicalUrl]);
  return {
    url
  };
};
//# sourceMappingURL=use-canonical-url.js.map